import type {ReactElement} from 'react';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {Checkbox} from '@Components/checkbox';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {OverlayTrigger} from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {doesAccountRequireReconnection, openSocialAccountsUpsellDialog} from '@Libraries/social-media-account.library';
import {useIsAccountReconnecting, useIsAnyAccountReconnecting, useReconnectAccount} from '@Components/social-media/social-media.hooks';
import {noop} from '@Utils/general.util';
import {SkeletonBrick} from '@Components/skeleton-brick';
import styles from './social-account-list-item.module.scss';
import {SocialMediaTestId} from '@/test-ids/social-media-test-id';

interface SocialAccountListItemProps {
  account: SocialAccount;
  isSelected?: boolean;
  isDisabled?: boolean;
  tooltip?: SocialAccountListItemTooltip;
  onCheck?: (idSocialMediaAccount: number) => void;
  onUncheck?: (idSocialMediaAccount: number) => void;
}

export interface SocialAccountListItemTooltip {
  text: string;
  icon: string;
  classes?: string;
}

export function SocialAccountListItem({
  account,
  isSelected = false,
  isDisabled = false,
  tooltip = undefined,
  onCheck = noop,
  onUncheck = noop,
}: SocialAccountListItemProps): ReactElement<any> {
  const [isChecked, setIsChecked] = useState(isSelected);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const {isSmallScreen, isExtraSmallScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isLocked = account.isLockedByPlanLimit;
  const {startReconnect} = useReconnectAccount();
  const isReconnecting = useIsAccountReconnecting(account.id);
  const isAnyAccountReconnecting = useIsAnyAccountReconnecting();
  const isBlockedByOtherReconnect = isAnyAccountReconnecting && !isReconnecting;

  useEffect(() => {
    setIsChecked(isSelected);
  }, [isSelected]);

  const onAccountClicked = (): void => {
    const newIsChecked = !isChecked;

    setIsChecked(newIsChecked);

    if (newIsChecked) {
      onCheck(account.id);
    } else {
      onUncheck(account.id);
    }
  };

  const onRowClicked = (): void => {
    if (isLocked) {
      openSocialAccountsUpsellDialog();
      return;
    }

    if (isRowUnselectable()) {
      return;
    }

    onAccountClicked();
  };

  const toolTipElement = useMemo((): ReactElement<any> | null => {
    if (!tooltip) {
      return null;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={
          <Tooltip
            id={`tooltip-overlay-${account.id}`}
            className={`_pointer-events-none ${isExtraSmallScreen || isSmallScreen ? styles.customTooltip : ''}`}
            style={{position: 'fixed'}}
          >
            {tooltip.text}
          </Tooltip>
        }
        delay={{show: 50, hide: 50}}
        placement="top"
        popperConfig={undefined}
        show={undefined}
        defaultShow={undefined}
        onHide={undefined}
        onToggle={undefined}
        target={undefined}
        flip={undefined}
        offset={undefined}
      >
        <Icon
          ref={tooltipRef}
          icon={tooltip.icon}
          className={`${styles.tooltipIcon} ${styles.actionIconPlacement} ${tooltip.classes ?? ''}`}
          size={IconSize.SIZE_ICON_20}
          type={IconType.NONE}
          shape={IconShape.NONE}
        />
      </OverlayTrigger>
    );
  }, [tooltip]);

  const getThemeClassesForRow = (): string => {
    if (doesAccountRequireReconnection(account)) {
      return styles.error;
    }

    if (isDisabled) {
      return styles.disabled;
    }

    return '';
  };

  const isRowUnselectable = (): boolean => {
    return isDisabled || doesAccountRequireReconnection(account);
  };

  const onFixThis = (): void => {
    startReconnect(account, {
      onReconnected: (accountId) => {
        onCheck(accountId);
      },
    });
  };

  const getPlanLimitUpsellIcon = (): ReactElement<any> => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        delay={{show: 50, hide: 50}}
        overlay={
          <Tooltip
            id={`plan-limit-tooltip-${account.id}`}
            className={`_pointer-events-none ${isExtraSmallScreen || isSmallScreen ? styles.customTooltip : ''}`}
            style={{position: 'fixed'}}
          >
            {window.i18next.t('pmwjs_plan_limit_one_active_account_tooltip')}
          </Tooltip>
        }
      >
        <Icon ref={tooltipRef} icon="icon-crown" className={styles.upsellIconPlacement} size={IconSize.SIZE_ICON_16} type={IconType.PREMIUM} shape={IconShape.SQUARE} />
      </OverlayTrigger>
    );
  };

  const getFixThisButton = (): ReactElement => {
    return (
      <Button
        type={Type.SECONDARY}
        size={Size.XSMALL}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-refresh"
        text={window.i18next.t('pmwjs_social_fix_this')}
        onClick={onFixThis}
        disabled={isBlockedByOtherReconnect}
        customClasses={styles.fixThisButtonPlacement}
      />
    );
  };

  const getActionElement = (): ReactElement<any> | null => {
    if (isLocked) {
      return getPlanLimitUpsellIcon();
    }

    if (doesAccountRequireReconnection(account)) {
      return getFixThisButton();
    }

    if (toolTipElement) {
      return toolTipElement;
    }

    return null;
  };

  const isDisabledState = (): boolean => {
    return isDisabled && !doesAccountRequireReconnection(account);
  };

  const getSkeletonRow = (): ReactElement => {
    return (
      <div className={`flex-h-row align-items-center spacing-p-2 radius-4 ${styles.listItem}`}>
        <SkeletonBrick height={16} width={16} className="spacing-m-r-4" />
        <SkeletonBrick height={32} width={32} className="radius-round spacing-m-r-4" />
        <SkeletonBrick height={16} width={140} />
      </div>
    );
  };

  if (isReconnecting) {
    return getSkeletonRow();
  }

  return (
    <ClickableDiv
      data-testid={SocialMediaTestId.WIZARD_ACCOUNT_ROW}
      className={`_cursor-pointer flex-h-row spacing-p-2 radius-4 ${getThemeClassesForRow()} ${styles.listItem} ${isChecked ? styles.selected : ''}`}
      onClick={onRowClicked}
    >
      <Checkbox className="spacing-m-r-4" isChecked={isChecked} />
      <SocialAccountLabel account={account} isDisabled={isDisabledState()} isConnectionExpired={doesAccountRequireReconnection(account)} />
      {getActionElement()}
    </ClickableDiv>
  );
}
